import { Trans, useLingui } from '@lingui/react/macro';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import type { SxProps, Theme } from '@mui/material';
import { Box } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { memo, useCallback } from 'react';
import { useProfilePoints } from '../../features/points/hooks/useProfilePoints.ts';
import { pointsAmountToDisplay } from '../../features/points/pointsCampaign.ts';
import useAppStore from '../../store/rootStore';
import {
  headerPointsGradientFillSx,
  headerPointsIconSx,
  headerPointsTriggerSx,
  headerPointsUnitSx,
  headerPointsValueSx,
} from './header_action_styles.ts';

interface PointsButtonProps {
  sx?: SxProps<Theme>;
}

/** Display value for the header pill, or null when it should stay hidden. */
function useHeaderPointsValue(): number | null {
  const pointsEnabled = useAppStore((state) => state.config?.features?.pointsEnabled);
  const showRealPoints = useAppStore((state) => state.config?.features?.showRealPoints ?? false);
  const isFullyAuthorized = useAppStore((state) => state.isFullyAuthorized());
  const collateralDecimals = useAppStore((state) => state.exchange?.token?.decimals ?? 6);
  const { data: points } = useProfilePoints();

  if (!pointsEnabled || !isFullyAuthorized || !showRealPoints || !points) return null;
  const value = pointsAmountToDisplay(points.total_points, collateralDecimals);
  return value > 0 ? value : null;
}

export const PointsButton = memo(function PointsButton({ sx }: PointsButtonProps) {
  const navigate = useNavigate();
  const { i18n } = useLingui();
  const pointsValue = useHeaderPointsValue();

  const handleClick = useCallback(() => {
    navigate({ to: '/points' });
  }, [navigate]);

  if (pointsValue == null) return null;

  return (
    <Box
      component="button"
      type="button"
      onClick={handleClick}
      sx={[headerPointsTriggerSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
    >
      <Box component="span" sx={headerPointsGradientFillSx}>
        <AutoAwesomeIcon aria-hidden sx={headerPointsIconSx} />
        <Box component="span" sx={headerPointsValueSx} className="numeric">
          {i18n.number(pointsValue)}
        </Box>
        <Box component="span" sx={headerPointsUnitSx} className="numeric">
          <Trans comment="Points unit suffix in the header pill">pts</Trans>
        </Box>
      </Box>
    </Box>
  );
});
